// slices/adminProviderSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import AdminProviderService from "../services/AdminProviderService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message; 
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ADMIN PROVIDER ROUTES THUNKS ====================

/**
 * Get all service providers with filtering, sorting, and pagination (Admin view)
 * GET /api/providers
 */
export const adminGetAllProviders = createAsyncThunk(
  'adminProvider/adminGetAllProviders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await AdminProviderService.getAllProviders(params);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch providers');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get service provider by ID (Admin view)
 * GET /api/providers/:id
 */
export const adminGetProviderById = createAsyncThunk(
  'adminProvider/adminGetProviderById',
  async ({ id, populate = null }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Provider ID is required');
      }
      const response = await AdminProviderService.getProviderById(id, populate);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch provider');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers by service type (Admin view)
 * GET /api/providers/type/:serviceType
 */
export const adminGetProvidersByServiceType = createAsyncThunk(
  'adminProvider/adminGetProvidersByServiceType',
  async ({ serviceType, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      if (!serviceType) {
        return rejectWithValue('Service type is required');
      }
      const response = await AdminProviderService.getProvidersByServiceType(serviceType, page, limit);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch providers by service type');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get featured service providers (Admin view)
 * GET /api/providers/featured
 */
export const adminGetFeaturedProviders = createAsyncThunk(
  'adminProvider/adminGetFeaturedProviders',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await AdminProviderService.getFeaturedProviders(page, limit);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch featured providers');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Delete any service provider
 * DELETE /api/providers/:id
 */
export const adminDeleteProvider = createAsyncThunk(
  'adminProvider/adminDeleteProvider',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Provider ID is required');
      }
      const response = await AdminProviderService.deleteProvider(id);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to delete provider');
      }
      return { ...response, id };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get any provider statistics
 * GET /api/providers/:id/statistics
 */
export const adminGetProviderStatistics = createAsyncThunk(
  'adminProvider/adminGetProviderStatistics',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Provider ID is required');
      }
      const response = await AdminProviderService.getProviderStatistics(id);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch provider statistics');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk update provider status
 * PATCH /api/providers/bulk/status
 */
export const adminBulkUpdateProviderStatus = createAsyncThunk(
  'adminProvider/adminBulkUpdateProviderStatus',
  async ({ ids, status, reason = null }, { rejectWithValue }) => {
    try {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        return rejectWithValue('Provider IDs array is required');
      }
      if (!status) {
        return rejectWithValue('Status is required');
      }
      const response = await AdminProviderService.bulkUpdateProviderStatus(ids, status, reason);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to bulk update provider status');
      }
      return { ...response, ids, status };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update provider verification status
 * PATCH /api/providers/:id/verification
 */
export const adminUpdateProviderVerificationStatus = createAsyncThunk(
  'adminProvider/adminUpdateProviderVerificationStatus',
  async ({ id, status, reason = null }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Provider ID is required');
      }
      if (!status) {
        return rejectWithValue('Status is required');
      }
      const response = await AdminProviderService.updateProviderVerificationStatus(id, status, reason);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to update verification status');
      }
      return { ...response, id, status, reason };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Toggle featured status
 * PATCH /api/providers/:id/featured
 */
export const adminToggleProviderFeatured = createAsyncThunk(
  'adminProvider/adminToggleProviderFeatured',
  async ({ id, isFeatured, duration = 30 }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue('Provider ID is required');
      }
      if (isFeatured === undefined || isFeatured === null) {
        return rejectWithValue('Featured status is required');
      }
      const response = await AdminProviderService.toggleProviderFeatured(id, isFeatured, duration);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to toggle featured status');
      }
      return { ...response, id, isFeatured };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers by user ID (admin can view any user's providers)
 * GET /api/providers/user/:userId
 */
export const adminGetProvidersByUserId = createAsyncThunk(
  'adminProvider/adminGetProvidersByUserId',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required');
      }
      const response = await AdminProviderService.getProvidersByUserId(userId);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch providers by user ID');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers overview statistics
 * GET /api/providers/statistics/overview
 */
export const adminGetProvidersOverview = createAsyncThunk(
  'adminProvider/adminGetProvidersOverview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminProviderService.getProvidersOverview();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch provider overview');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers by status (Admin view)
 * GET /api/providers/status/:status
 */
export const adminGetProvidersByStatus = createAsyncThunk(
  'adminProvider/adminGetProvidersByStatus',
  async ({ status, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      if (!status) {
        return rejectWithValue('Status is required');
      }
      const response = await AdminProviderService.getProvidersByStatus(status, page, limit);
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch providers by status');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get provider filters (for dropdowns and filter options)
 * GET /api/providers/filters
 */
export const adminGetProviderFilters = createAsyncThunk(
  'adminProvider/adminGetProviderFilters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminProviderService.getProviderFilters();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to fetch provider filters');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Export providers data
 * GET /api/providers/export
 */
export const adminExportProvidersData = createAsyncThunk(
  'adminProvider/adminExportProvidersData',
  async ({ format = 'csv', filters = {} }, { rejectWithValue }) => {
    try {
      const response = await AdminProviderService.exportProvidersData(format, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SUPER ADMIN ROUTES THUNKS ====================

/**
 * Clear location cache
 * POST /api/providers/clear-cache
 */
export const adminClearLocationCache = createAsyncThunk(
  'adminProvider/adminClearLocationCache',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminProviderService.clearLocationCache();
      if (!response.success && response.success !== undefined) {
        return rejectWithValue(response.message || 'Failed to clear location cache');
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Data states
  providers: [],
  currentProvider: null,
  featuredProviders: [],
  userProviders: [],
  providerStats: null,
  providerOverview: null,
  providerFilters: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  },
  
  // Filter/Search
  filters: {},
  searchQuery: '',
  selectedStatus: null,
  
  // Loading states
  loading: false,
  deleting: false,
  updating: false,
  exporting: false,
  
  // Error states
  error: null,
  deleteError: null,
  updateError: null,
  exportError: null,
  
  // Success states
  deleteSuccess: false,
  updateSuccess: false,
  
  // UI states
  isRefreshing: false,
  hasMore: true,
  selectedProviderId: null,
  selectedUserId: null,
  
  // Cache
  cache: {},
  lastFetch: null
};

const adminProviderSlice = createSlice({
  name: 'adminProvider',
  initialState,
  reducers: {
    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.deleteError = null;
      state.updateError = null;
      state.exportError = null;
    },
    
    // Reset update state
    resetUpdateState: (state) => {
      state.updateSuccess = false;
      state.updateError = null;
    },
    
    // Reset delete state
    resetDeleteState: (state) => {
      state.deleteSuccess = false;
      state.deleteError = null;
    },
    
    // Reset provider states
    resetAdminProviderState: (state) => {
      state.providers = [];
      state.currentProvider = null;
      state.providerStats = null;
      state.providerOverview = null;
      state.providerFilters = null;
      state.error = null;
      state.deleteError = null;
      state.updateError = null;
      state.deleteSuccess = false;
      state.updateSuccess = false;
      state.cache = {};
      state.lastFetch = null;
    },
    
    // Set filters
    setAdminFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearAdminFilters: (state) => {
      state.filters = {};
      state.searchQuery = '';
      state.selectedStatus = null;
    },
    
    // Set search query
    setAdminSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    
    // Set status filter
    setSelectedStatus: (state, action) => {
      state.selectedStatus = action.payload;
    },
    
    // Set pagination
    setAdminPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set refreshing
    setAdminRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
    
    // Select provider
    selectAdminProvider: (state, action) => {
      state.selectedProviderId = action.payload;
    },
    
    // Select user
    selectUser: (state, action) => {
      state.selectedUserId = action.payload;
    },
    
    // Clear current provider
    clearCurrentAdminProvider: (state) => {
      state.currentProvider = null;
    },
    
    // Update provider in list
    updateProviderInAdminList: (state, action) => {
      const updatedProvider = action.payload;
      const index = state.providers.findIndex(p => p._id === updatedProvider._id);
      if (index !== -1) {
        state.providers[index] = updatedProvider;
      }
    },
    
    // Remove provider from list
    removeProviderFromAdminList: (state, action) => {
      const id = action.payload;
      state.providers = state.providers.filter(p => p._id !== id);
    },
    
    // Reset admin provider list (keep other states)
    resetAdminProviderList: (state) => {
      state.providers = [];
      state.pagination = initialState.pagination;
      state.hasMore = true;
    },
    
    // ✅ NEW: Clear provider cache
    adminClearProviderCache: (state) => {
      state.cache = {};
      state.lastFetch = null;
      console.log('✅ Admin provider cache cleared');
    },
    
    // ✅ NEW: Update provider status in list (optimistic update)
    adminUpdateProviderStatus: (state, action) => {
      const { id, status } = action.payload;
      const index = state.providers.findIndex(p => p._id === id || p.id === id);
      if (index !== -1) {
        state.providers[index] = {
          ...state.providers[index],
          verificationStatus: status,
          verified: status === 'verified'
        };
      }
      if (state.currentProvider && (state.currentProvider._id === id || state.currentProvider.id === id)) {
        state.currentProvider = {
          ...state.currentProvider,
          verificationStatus: status,
          verified: status === 'verified'
        };
      }
      console.log(`✅ Provider ${id} status updated to ${status} in state`);
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== ADMIN GET ALL PROVIDERS ==========
      .addCase(adminGetAllProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isRefreshing = true;
      })
      .addCase(adminGetAllProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.lastFetch = Date.now();

        const data = action.payload?.data;

        state.providers = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];

        const apiPagination = data?.pagination || action.payload?.pagination;

        state.pagination = {
          total: apiPagination?.total ?? 0,
          page: apiPagination?.page ?? 1,
          limit: apiPagination?.limit ?? 20,
          pages: apiPagination?.pages ?? apiPagination?.totalPages ?? 0,
          totalPages: apiPagination?.pages ?? apiPagination?.totalPages ?? 0
        };

        state.hasMore = state.pagination.page < state.pagination.totalPages;

        console.log('✅ ADMIN PROVIDERS LOADED:', state.providers.length);
      })
      .addCase(adminGetAllProviders.rejected, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.error = action.payload || 'Failed to fetch providers';
      })
      
      // ========== ADMIN GET PROVIDER BY ID ==========
      .addCase(adminGetProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProvider = action.payload.data || null;
        // Update in cache
        if (state.currentProvider) {
          state.cache[state.currentProvider._id] = state.currentProvider;
        }
      })
      .addCase(adminGetProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch provider';
      })
      
      // ========== ADMIN GET PROVIDERS BY SERVICE TYPE ==========
      .addCase(adminGetProvidersByServiceType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProvidersByServiceType.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetch = Date.now();

        const data = action.payload?.data;

        state.providers = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];

        const apiPagination = data?.pagination || action.payload?.pagination;

        state.pagination = {
          total: apiPagination?.total ?? 0,
          page: apiPagination?.page ?? 1,
          limit: apiPagination?.limit ?? 20,
          pages: apiPagination?.pages ?? apiPagination?.totalPages ?? 0,
          totalPages: apiPagination?.pages ?? apiPagination?.totalPages ?? 0
        };

        state.hasMore = state.pagination.page < state.pagination.pages;
      })
      .addCase(adminGetProvidersByServiceType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by service type';
      })
      
      // ========== ADMIN GET FEATURED PROVIDERS ==========
      .addCase(adminGetFeaturedProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetFeaturedProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredProviders = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
      })
      .addCase(adminGetFeaturedProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch featured providers';
      })
      
      // ========== ADMIN DELETE PROVIDER ==========
      .addCase(adminDeleteProvider.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(adminDeleteProvider.fulfilled, (state, action) => {
        state.deleting = false;
        state.deleteSuccess = true;
        state.providers = state.providers.filter(p => p._id !== action.payload.id);
        if (state.currentProvider && state.currentProvider._id === action.payload.id) {
          state.currentProvider = null;
        }
        if (state.selectedProviderId === action.payload.id) {
          state.selectedProviderId = null;
        }
        // Clear from cache
        delete state.cache[action.payload.id];
      })
      .addCase(adminDeleteProvider.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to delete provider';
        state.deleteSuccess = false;
      })
      
      // ========== ADMIN GET PROVIDER STATISTICS ==========
      .addCase(adminGetProviderStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProviderStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.providerStats = action.payload.data || null;
      })
      .addCase(adminGetProviderStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch provider statistics';
      })
      
      // ========== ADMIN BULK UPDATE PROVIDER STATUS ==========
      .addCase(adminBulkUpdateProviderStatus.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(adminBulkUpdateProviderStatus.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const { ids, status } = action.payload;
        state.providers = state.providers.map(p => {
          if (ids.includes(p._id)) {
            return { 
              ...p, 
              verificationStatus: status, 
              verified: status === 'verified' 
            };
          }
          return p;
        });
        if (state.currentProvider && ids.includes(state.currentProvider._id)) {
          state.currentProvider = {
            ...state.currentProvider,
            verificationStatus: status,
            verified: status === 'verified'
          };
        }
        // Update cache
        ids.forEach(id => {
          if (state.cache[id]) {
            state.cache[id] = {
              ...state.cache[id],
              verificationStatus: status,
              verified: status === 'verified'
            };
          }
        });
      })
      .addCase(adminBulkUpdateProviderStatus.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to bulk update provider status';
        state.updateSuccess = false;
      })
      
      // ========== ADMIN UPDATE PROVIDER VERIFICATION STATUS ==========
      .addCase(adminUpdateProviderVerificationStatus.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
     .addCase(
  adminUpdateProviderVerificationStatus.fulfilled,
  (state, action) => {
    state.updating = false;
    state.updateSuccess = true;

    const { id, status } = action.payload;

    // Find provider using either API "id" or MongoDB "_id"
    const index = state.providers.findIndex(
      (provider) =>
        provider.id === id ||
        provider._id === id
    );

    // Update provider in the main list
    if (index !== -1) {
      state.providers[index] = {
        ...state.providers[index],
        verificationStatus: status,
        verified: status === 'verified'
      };
    }

    // Update currently selected provider
    if (
      state.currentProvider &&
      (
        state.currentProvider.id === id ||
        state.currentProvider._id === id
      )
    ) {
      state.currentProvider = {
        ...state.currentProvider,
        verificationStatus: status,
        verified: status === 'verified'
      };
    }

    // Update cache using the same ID
    if (state.cache[id]) {
      state.cache[id] = {
        ...state.cache[id],
        verificationStatus: status,
        verified: status === 'verified'
      };
    }

    console.log(
      `Provider ${id} verification status updated to ${status}`
    );
  }
)
      .addCase(adminUpdateProviderVerificationStatus.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update verification status';
        state.updateSuccess = false;
        console.error('❌ Update verification status failed:', action.payload);
      })
      
      // ========== ADMIN TOGGLE PROVIDER FEATURED ==========
      .addCase(adminToggleProviderFeatured.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(adminToggleProviderFeatured.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const { id, isFeatured } = action.payload;
        const index = state.providers.findIndex(p => p._id === id);
        if (index !== -1) {
          state.providers[index] = {
            ...state.providers[index],
            isFeatured: isFeatured
          };
        }
        if (state.currentProvider && state.currentProvider._id === id) {
          state.currentProvider = {
            ...state.currentProvider,
            isFeatured: isFeatured
          };
        }
        // Update cache
        if (state.cache[id]) {
          state.cache[id] = {
            ...state.cache[id],
            isFeatured: isFeatured
          };
        }
        const featuredIndex = state.featuredProviders.findIndex(p => p._id === id);
        if (featuredIndex !== -1) {
          if (!isFeatured) {
            state.featuredProviders.splice(featuredIndex, 1);
          } else {
            state.featuredProviders[featuredIndex] = {
              ...state.featuredProviders[featuredIndex],
              isFeatured: isFeatured
            };
          }
        }
      })
      .addCase(adminToggleProviderFeatured.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to toggle featured status';
        state.updateSuccess = false;
      })
      
      // ========== ADMIN GET PROVIDERS BY USER ID ==========
      .addCase(adminGetProvidersByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProvidersByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.userProviders = action.payload.data || [];
      })
      .addCase(adminGetProvidersByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by user ID';
      })
      
      // ========== ADMIN GET PROVIDERS OVERVIEW ==========
      .addCase(adminGetProvidersOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProvidersOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.providerOverview = action.payload.data || null;
      })
      .addCase(adminGetProvidersOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch provider overview';
      })
      
      // ========== ADMIN GET PROVIDERS BY STATUS ==========
      .addCase(adminGetProvidersByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProvidersByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetch = Date.now();

        const data = action.payload?.data;

        state.providers = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];

        const apiPagination = data?.pagination || action.payload?.pagination;

        state.pagination = {
          total: apiPagination?.total ?? 0,
          page: apiPagination?.page ?? 1,
          limit: apiPagination?.limit ?? 20,
          pages: apiPagination?.pages ?? apiPagination?.totalPages ?? 0,
          totalPages: apiPagination?.pages ?? apiPagination?.totalPages ?? 0
        };

        state.hasMore = state.pagination.page < state.pagination.pages;
      })
      .addCase(adminGetProvidersByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by status';
      })
      
      // ========== ADMIN GET PROVIDER FILTERS ==========
      .addCase(adminGetProviderFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminGetProviderFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.providerFilters = action.payload.data || null;
      })
      .addCase(adminGetProviderFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch provider filters';
      })
      
      // ========== ADMIN CLEAR LOCATION CACHE ==========
      .addCase(adminClearLocationCache.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminClearLocationCache.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(adminClearLocationCache.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to clear location cache';
      })
      
      // ========== ADMIN EXPORT PROVIDERS DATA ==========
      .addCase(adminExportProvidersData.pending, (state) => {
        state.exporting = true;
        state.exportError = null;
      })
      .addCase(adminExportProvidersData.fulfilled, (state) => {
        state.exporting = false;
      })
      .addCase(adminExportProvidersData.rejected, (state, action) => {
        state.exporting = false;
        state.exportError = action.payload || 'Failed to export providers data';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectAdminProviderState = (state) => state.adminProvider;

// ==================== PROVIDER SELECTORS ====================

export const selectAdminAllProviders = createSelector(
  selectAdminProviderState,
  (adminProvider) => {
    if (!adminProvider || !adminProvider.providers) {
      return [];
    }
    return Array.isArray(adminProvider.providers) ? adminProvider.providers : [];
  }
);

export const selectAdminCurrentProvider = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.currentProvider || null
);

export const selectAdminFeaturedProviders = createSelector(
  selectAdminProviderState,
  (adminProvider) => {
    if (!adminProvider || !adminProvider.featuredProviders) {
      return [];
    }
    return Array.isArray(adminProvider.featuredProviders) ? adminProvider.featuredProviders : [];
  }
);

export const selectAdminUserProviders = createSelector(
  selectAdminProviderState,
  (adminProvider) => {
    if (!adminProvider || !adminProvider.userProviders) {
      return [];
    }
    return Array.isArray(adminProvider.userProviders) ? adminProvider.userProviders : [];
  }
);

export const selectAdminProviderStats = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.providerStats || null
);

export const selectAdminProviderOverview = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.providerOverview || null
);

export const selectAdminProviderFilters = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.providerFilters || null
);

export const selectAdminPagination = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.pagination || { total: 0, page: 1, limit: 10, totalPages: 0 }
);

export const selectAdminLoading = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.loading || false
);

export const selectAdminDeleting = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.deleting || false
);

export const selectAdminUpdating = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.updating || false
);

export const selectAdminDeleteError = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.deleteError || null
);

export const selectAdminUpdateError = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.updateError || null
);

export const selectAdminHasMore = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.hasMore || false
);

export const selectAdminIsRefreshing = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.isRefreshing || false
);

export const selectAdminSelectedProviderId = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.selectedProviderId || null
);

export const selectAdminSelectedUserId = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.selectedUserId || null
);

export const selectAdminFilters = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.filters || {}
);

export const selectAdminSearchQuery = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.searchQuery || ''
);

export const selectAdminSelectedStatus = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.selectedStatus || null
);

export const selectAdminExporting = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.exporting || false
);

export const selectAdminExportError = createSelector(
  selectAdminProviderState,
  (adminProvider) => adminProvider?.exportError || null
);

// ==================== STATISTICS SELECTORS ====================

export const selectAdminStatusCounts = createSelector(
  selectAdminAllProviders,
  (providers) => {
    const counts = {
      pending: 0,
      verified: 0,
      rejected: 0,
      suspended: 0,
      total: providers.length
    };
    
    providers.forEach(p => {
      if (p.verificationStatus && counts.hasOwnProperty(p.verificationStatus)) {
        counts[p.verificationStatus]++;
      }
    });
    
    return counts;
  }
);

export const selectAdminServiceTypeCounts = createSelector(
  selectAdminAllProviders,
  (providers) => {
    const counts = {};
    providers.forEach(p => {
      if (p.serviceType) {
        counts[p.serviceType] = (counts[p.serviceType] || 0) + 1;
      }
    });
    return counts;
  }
);

export const selectAdminAverageRating = createSelector(
  selectAdminAllProviders,
  (providers) => {
    const rated = providers.filter(p => p.rating && p.rating > 0);
    if (rated.length === 0) return 0;
    const sum = rated.reduce((acc, p) => acc + p.rating, 0);
    return parseFloat((sum / rated.length).toFixed(1));
  }
);

// ==================== DERIVED SELECTORS ====================

export const selectAdminFilteredProviders = createSelector(
  [selectAdminAllProviders, selectAdminFilters, selectAdminSearchQuery, selectAdminSelectedStatus],
  (providers, filters, searchQuery, selectedStatus) => {
    let filtered = [...providers];
    
    if (selectedStatus) {
      filtered = filtered.filter(p => p.verificationStatus === selectedStatus);
    }
    
    if (filters.serviceType) {
      filtered = filtered.filter(p => p.serviceType === filters.serviceType);
    }
    
    if (filters.verified !== undefined) {
      filtered = filtered.filter(p => p.verified === filters.verified);
    }
    
    if (filters.isFeatured !== undefined) {
      filtered = filtered.filter(p => p.isFeatured === filters.isFeatured);
    }
    
    if (filters.minRating) {
      filtered = filtered.filter(p => p.rating >= filters.minRating);
    }
    
    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      filtered = filtered.filter(p => new Date(p.createdAt) >= dateFrom);
    }
    
    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      filtered = filtered.filter(p => new Date(p.createdAt) <= dateTo);
    }
    
    if (filters.languages && filters.languages.length > 0) {
      filtered = filtered.filter(p => 
        p.languages && p.languages.some(lang => filters.languages.includes(lang))
      );
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.businessName?.toLowerCase().includes(query) ||
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.serviceType?.toLowerCase().includes(query) ||
        p.address?.city?.toLowerCase().includes(query) ||
        p.address?.country?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query) ||
        p.phone?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }
);

export const selectAdminProvidersByStatus = (status) => createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.verificationStatus === status)
);

export const selectAdminPendingProviders = createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.verificationStatus === 'pending')
);

export const selectAdminVerifiedProviders = createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.verificationStatus === 'verified')
);

export const selectAdminRejectedProviders = createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.verificationStatus === 'rejected')
);

export const selectAdminSuspendedProviders = createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.verificationStatus === 'suspended')
);

export const selectAdminFeaturedProvidersOnly = createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.isFeatured === true)
);

export const selectAdminNonFeaturedProviders = createSelector(
  selectAdminAllProviders,
  (providers) => providers.filter(p => p.isFeatured === false)
);

export const selectAdminProviderById = (id) => createSelector(
  selectAdminAllProviders,
  (providers) => providers.find(p => p.id === id || p._id === id)
);

// ==================== EXPORTS ====================

export const {
  clearErrors,
  resetUpdateState,
  resetDeleteState,
  resetAdminProviderState,
  setAdminFilters,
  clearAdminFilters,
  setAdminSearchQuery,
  setSelectedStatus,
  setAdminPagination,
  setAdminRefreshing,
  selectAdminProvider,
  selectUser,
  clearCurrentAdminProvider,
  updateProviderInAdminList,
  removeProviderFromAdminList,
  resetAdminProviderList,
  adminClearProviderCache,  // ✅ EXPORT THIS
  adminUpdateProviderStatus // ✅ EXPORT THIS
} = adminProviderSlice.actions;

export default adminProviderSlice.reducer;